import type { ChatToken } from "schemas/generated/chatMessage";

import { parseJsonOrNull } from "replo-utils/lib/json";
import { decodeURIComponentSafe } from "replo-utils/lib/string";
import { chatTokenSchema } from "schemas/chatMessage";

export type ChatTokenSegment = {
  type: "chat-token";
  content: string;
  token: ChatToken;
  id: string;
};

export type ParsedSegment =
  | {
      type: "text";
      content: string;
      id: string;
    }
  | ChatTokenSegment;

export function isChatTokenSegment(
  segment: ParsedSegment,
): segment is ChatTokenSegment {
  return segment.type === "chat-token";
}

/**
 * Parses a string containing <chat-token> tags and returns segments. Segments is a list of text and chat-token
 * objects and we use that to render the entire message.
 */
export function parseChatTokens(input: string): ParsedSegment[] {
  const segments: ParsedSegment[] = [];
  const chatTokenRegex = /<chat-token([^>]*)>(.*?)<\/chat-token>/gs;

  let lastIndex = 0;
  let match = chatTokenRegex.exec(input);

  while (match !== null) {
    if (match.index > lastIndex) {
      const textContent = input.slice(lastIndex, match.index);
      if (textContent.trim()) {
        segments.push({
          type: "text",
          content: textContent,
          id: `text-${segments.length}`,
        });
      }
    }

    try {
      const attributeString = match[1] ?? "";
      const innerContent = match[2] ?? "";

      const attributePayload = extractPayloadFromAttributes(attributeString);
      const contentPayload = decodeURIComponentSafe(innerContent.trim());

      const rawPayload: string = attributePayload ?? contentPayload ?? "";
      const tokenData = chatTokenSchema.parse(parseJsonOrNull(rawPayload));

      segments.push({
        type: "chat-token",
        content: match[0],
        token: tokenData,
        id: `element-${segments.length}`,
      });
    } catch (error) {
      console.warn("Failed to parse chat-token:", {
        error,
      });
      segments.push({
        type: "text",
        content: "",
        id: `text-${segments.length}`,
      });
    }

    lastIndex = match.index + match[0].length;
    match = chatTokenRegex.exec(input);
  }

  // Add remaining text after the last chat-token
  if (lastIndex < input.length) {
    const textContent = input.slice(lastIndex);
    if (textContent.trim()) {
      segments.push({
        type: "text",
        content: textContent,
        id: `text-${segments.length}`,
      });
    }
  }

  return segments;
}

const DATA_TOKEN_REGEX = /data-token\s*=\s*"([^"]+)"/i;

const extractPayloadFromAttributes = (attributes: string): string | null => {
  const attrMatch = DATA_TOKEN_REGEX.exec(attributes);
  if (!attrMatch) {
    return null;
  }

  const encodedValue = attrMatch[1] ?? "";
  return decodeURIComponentSafe(encodedValue) ?? encodedValue;
};
